import React, { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Checkbox,
  FormControlLabel, Alert, Link as MuiLink,
} from "@mui/material";
import { login } from "../api";
import { C } from "../constants/colors";
import PasswordField, { fieldTone } from "../components/PasswordField";
import { adoptWorkspaceUser } from "../utils/workspaceIsolation";
import { formatApiError } from "../utils/apiError";
import { isValidEmail, textFieldToneSx } from "../utils/passwordRules";
import { useAppNotify } from "../components/AppNotify";
import AuthLayout from "../layout/AuthLayout";
import { authButtonSx, authLabelSx } from "../theme/authStyles";

const OKRION_REGISTER_URL = "https://app.okrion.ai/select-role";

export default function Login({ onLogin, goToForgot }) {
  const { notify } = useAppNotify();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [pwTouched, setPwTouched] = useState(false);
  const [error, setError] = useState("");
  const [remember, setRemember] = useState(false);
  const [authNotice, setAuthNotice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const cached = localStorage.getItem("ap_login_cache");
    if (cached) {
      try {
        const { email: ce, password: cp, remember: cr } = JSON.parse(cached);
        if (ce) setEmail(ce);
        if (cp) setPassword(cp);
        if (cr) setRemember(true);
      } catch (_) {}
    } else {
      const session = sessionStorage.getItem("ap_login_session");
      if (session) {
        try {
          const { email: se, password: sp } = JSON.parse(session);
          if (se) setEmail(se);
          if (sp) setPassword(sp);
        } catch (_) {}
      }
    }
    try {
      const notice = sessionStorage.getItem("ap_auth_notice");
      if (notice === "session_expired") {
        setAuthNotice("Your session expired. Sign in again to continue.");
        sessionStorage.removeItem("ap_auth_notice");
        notify({ type: "warning", title: "Session expired", message: "Sign in again to continue." });
      } else if (notice === "account_created") {
        setAuthNotice("Account created. Sign in with your new password.");
        sessionStorage.removeItem("ap_auth_notice");
        notify({ type: "success", title: "Account created", message: "Sign in with your new password." });
      } else if (notice === "signed_out") {
        setAuthNotice("You have signed out.");
        sessionStorage.removeItem("ap_auth_notice");
      }
    } catch (_) {}
  }, [notify]);

  const _saveSession = (em, pw) => {
    sessionStorage.setItem("ap_login_session", JSON.stringify({ email: em, password: pw }));
    if (remember) {
      localStorage.setItem("ap_login_cache", JSON.stringify({ email: em, password: pw, remember: true }));
    }
  };

  const handleEmailChange = (e) => { const val = e.target.value; setEmail(val); _saveSession(val, password); };
  const handlePasswordChange = (e) => { const val = e.target.value; setPassword(val); setPwTouched(true); _saveSession(email, val); };

  const handleRememberChange = (e) => {
    const checked = e.target.checked;
    setRemember(checked);
    if (checked) {
      localStorage.setItem("ap_login_cache", JSON.stringify({ email, password, remember: true }));
    } else {
      localStorage.removeItem("ap_login_cache");
    }
  };

  const emailOk = isValidEmail(email);
  const pwOk = password.length > 0;

  const handleRegisterInOkrion = () => {
    window.location.href = OKRION_REGISTER_URL;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setEmailTouched(true);
    setPwTouched(true);

    if (!emailOk) { setError("Enter a valid email address"); notify({ type: "error", message: "Enter a valid email address" }); return; }
    if (!pwOk) { setError("Enter your password"); notify({ type: "error", message: "Enter your password" }); return; }

    setBusy(true);
    try {
      const res = await login({ email, password });
      const token = String(res.data.token || "").trim().replace(/^Bearer\s+/i, "");
      if (!token || token.split(".").length !== 3) {
        setError("Login succeeded but token was invalid — try again");
        notify({ type: "error", message: "Sign-in did not finish. Please try again.", title: "Could not sign in" });
        setBusy(false);
        return;
      }
      adoptWorkspaceUser(res.data.user);
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      localStorage.setItem("ap_user", JSON.stringify(res.data.user));
      sessionStorage.removeItem("ap_login_session");
      if (!remember) localStorage.removeItem("ap_login_cache");
      onLogin(res.data.user);
    } catch (err) {
      const msg = formatApiError(err, "Login failed");
      setError(msg);
      notify({ type: "error", title: "Login failed", message: msg });
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Use your OKRion email and password to continue."
      footer={
        <>
          Don&apos;t have an account?{" "}
          <MuiLink component="button" type="button" onClick={handleRegisterInOkrion} sx={{ fontWeight: 600, color: C.primary }}>
            Register in OKRion
          </MuiLink>
        </>
      }
    >
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Typography sx={authLabelSx}>Email</Typography>
        <TextField
          fullWidth
          type="email"
          placeholder="name@institution.edu"
          value={email}
          onChange={handleEmailChange}
          onBlur={() => setEmailTouched(true)}
          autoComplete="email"
          sx={{ ...textFieldToneSx(fieldTone({ value: email, touched: emailTouched, valid: emailOk }), C), mb: 2 }}
        />

        <PasswordField
          label="Password"
          value={password}
          onChange={handlePasswordChange}
          autoComplete="current-password"
          tone={fieldTone({ value: password, touched: pwTouched, valid: pwOk })}
        />

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5, mt: -0.25 }}>
          <FormControlLabel
            sx={{ ml: -0.5 }}
            control={<Checkbox size="small" checked={remember} onChange={handleRememberChange} />}
            label={<Typography sx={{ fontSize: 13, color: C.muted, fontWeight: 400 }}>Remember me</Typography>}
          />
          <MuiLink
            component="button" type="button" underline="hover"
            onClick={() => goToForgot?.()}
            sx={{ fontSize: 13, fontWeight: 600, color: C.primary }}
          >
            Forgot password?
          </MuiLink>
        </Box>

        {authNotice && <Alert severity="info" sx={{ mb: 2, borderRadius: "8px" }}>{authNotice}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>{error}</Alert>}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={busy}
          sx={authButtonSx}
        >
          {busy ? "Signing in…" : "Sign in"}
        </Button>
      </Box>
    </AuthLayout>
  );
}
